import type { PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable, RecordKey } from '@ricoh/ria.client.core/types/core/data.type';

import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternValueSystemValue, PatternValueSystemValueBatch } from '~/common/model/api/batch-type';
import type { PatternBatch, PatternDocument } from '~/common/model/api/batch-type/pattern';

import { TRANSFORMATION_TEXT } from '~/common/constants/feature/settings/batch-type-library';

import type { CreatePatternBatchFormFieldPattern, CreatePatternBatchReturn, PatternBatchPathMap } from './_batch.type';
import type { PatternAccessScope } from './_common.type';

import { BATCH_PATH, DOCUMENT_PATH_BATCH_PREFIX } from './_path';

const batchPathMap = Object.entries<PatternValueSystemValueBatch, string>(BATCH_PATH).reduce(
  (prev, [pathKey, path]) => {
    prev.fromSystemValue[pathKey] = path;
    prev.toSystemValue[path] = pathKey;

    return prev;
  },
  {
    fromSystemValue: {},
    toSystemValue: {},
  } as PatternBatchPathMap,
);

/** PatternBatch Value. */
export const patternBatchValue = {
  batchField: {
    toPath: (name: string): string => `fields['${name}'].value`,
    fromPath: (path: string): string | undefined => {
      const result = /^fields\['(.+?)'\]\.value$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },

  batchVariable: {
    toPath: (name: string): string => `variables['${name}'].value`,
    fromPath: (path: string): string | undefined => {
      const result = /^variables\['(.+?)'\]\.value$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },

  batchBusinessRule: {
    toPath: (name: string): string => `rules['${name}']`,
    fromPath: (path: string): string | undefined => {
      const result = /^rules\['(.+?)'\]$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },
};

/** Create PatternBatch. */
export const createPatternBatch = {
  batchPath: <TScope extends PatternAccessScope>(value: string, scope: TScope, transformationText: boolean): Nullable<CreatePatternBatchReturn<TScope>> => {
    const path = batchPathMap.fromSystemValue[value];
    if (!path) {
      return undefined;
    }

    const pattern: PatternBatch | PatternDocument =
      scope === 'batch'
        ? {
            $is: 'batch',
            path,
          }
        : {
            $is: 'document',
            path: `${DOCUMENT_PATH_BATCH_PREFIX}${path}`,
          };

    switch (value as PatternValueSystemValue) {
      case 'createdAt':
      case 'lastUpdatedAt': {
        pattern.type = 'datetime';
        break;
      }

      case 'createdAtDate':
      case 'lastUpdatedAtDate': {
        pattern.type = 'date';
        break;
      }

      case 'createdAtTime':
      case 'lastUpdatedAtTime': {
        pattern.type = 'time';
        break;
      }

      default: {
        // Do nothing
        break;
      }
    }

    return {
      ...pattern,
      transformations: transformationText ? [TRANSFORMATION_TEXT] : undefined,
    } as CreatePatternBatchReturn<TScope>;
  },

  batchField: <TScope extends PatternAccessScope>(fieldName: string, scope: TScope): CreatePatternBatchReturn<TScope> => {
    const path = patternBatchValue.batchField.toPath(fieldName);

    return (
      scope === 'batch'
        ? {
            $is: 'batch',
            path,
          }
        : {
            $is: 'document',
            path: `${DOCUMENT_PATH_BATCH_PREFIX}${path}`,
          }
    ) as CreatePatternBatchReturn<TScope>;
  },

  batchVariable: <TScope extends PatternAccessScope>(variableName: string, scope: TScope): CreatePatternBatchReturn<TScope> => {
    const path = patternBatchValue.batchVariable.toPath(variableName);

    return (
      scope === 'batch'
        ? {
            $is: 'batch',
            path,
          }
        : {
            $is: 'document',
            path: `${DOCUMENT_PATH_BATCH_PREFIX}${path}`,
          }
    ) as CreatePatternBatchReturn<TScope>;
  },

  batchBusinessRule: <TScope extends PatternAccessScope>(ruleName: string, scope: TScope): CreatePatternBatchReturn<TScope> => {
    const path = patternBatchValue.batchBusinessRule.toPath(ruleName);

    return (
      scope === 'batch'
        ? {
            $is: 'batch',
            path,
          }
        : {
            $is: 'document',
            path: `${DOCUMENT_PATH_BATCH_PREFIX}${path}`,
          }
    ) as CreatePatternBatchReturn<TScope>;
  },
};

const createPatternBatchFormFieldBatchPath = {
  tracking: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternBatch | PatternDocument, patternPath: string) => {
    if (pattern.type === 'datetime') {
      return {
        tabKey,
        value: (patternPath === BATCH_PATH.createdAt ? 'createdAt' : 'lastUpdatedAt') satisfies PatternValueSystemValueBatch,
      };
    }

    if (pattern.type === 'date') {
      return {
        tabKey,
        value: (patternPath === BATCH_PATH.createdAt ? 'createdAtDate' : 'lastUpdatedAtDate') satisfies PatternValueSystemValueBatch,
      };
    }

    if (pattern.type === 'time') {
      return {
        tabKey,
        value: (patternPath === BATCH_PATH.createdAt ? 'createdAtTime' : 'lastUpdatedAtTime') satisfies PatternValueSystemValueBatch,
      };
    }

    return undefined;
  },
};

/** Create PatternBatch FormField. */
export const createPatternBatchFormField = {
  batchPath: <TTabKey extends RecordKey, TScope extends PatternAccessScope>(
    tabKey: TTabKey,
    pattern: CreatePatternBatchFormFieldPattern<TScope>,
  ): Nullable<PatternFormFieldItem<TTabKey>> => {
    let patternPath;
    if (isType<PatternBatch>(pattern, 'batch')) {
      patternPath = pattern.path;
    } else if (isType<PatternDocument>(pattern, 'document') && pattern.path.startsWith(DOCUMENT_PATH_BATCH_PREFIX)) {
      patternPath = pattern.path.substring(DOCUMENT_PATH_BATCH_PREFIX.length);
    } else {
      return undefined;
    }

    if (patternPath === BATCH_PATH.createdAt || patternPath === BATCH_PATH.lastUpdatedAt) {
      return createPatternBatchFormFieldBatchPath.tracking(tabKey, pattern, patternPath);
    }

    const value = batchPathMap.toSystemValue[patternPath];
    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  batchField: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternBatch | PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    let patternPath;
    if (isType<PatternBatch>(pattern, 'batch')) {
      patternPath = pattern.path;
    } else if (isType<PatternDocument>(pattern, 'document') && pattern.path.startsWith(DOCUMENT_PATH_BATCH_PREFIX)) {
      patternPath = pattern.path.substring(DOCUMENT_PATH_BATCH_PREFIX.length);
    } else {
      return undefined;
    }

    const value = patternBatchValue.batchField.fromPath(patternPath);
    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  batchVariable: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternBatch | PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    let patternPath;
    if (isType<PatternBatch>(pattern, 'batch')) {
      patternPath = pattern.path;
    } else if (isType<PatternDocument>(pattern, 'document') && pattern.path.startsWith(DOCUMENT_PATH_BATCH_PREFIX)) {
      patternPath = pattern.path.substring(DOCUMENT_PATH_BATCH_PREFIX.length);
    } else {
      return undefined;
    }

    const value = patternBatchValue.batchVariable.fromPath(patternPath);
    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  batchBusinessRule: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternBatch | PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    let patternPath;
    if (isType<PatternBatch>(pattern, 'batch')) {
      patternPath = pattern.path;
    } else if (isType<PatternDocument>(pattern, 'document') && pattern.path.startsWith(DOCUMENT_PATH_BATCH_PREFIX)) {
      patternPath = pattern.path.substring(DOCUMENT_PATH_BATCH_PREFIX.length);
    } else {
      return undefined;
    }

    const value = patternBatchValue.batchBusinessRule.fromPath(patternPath);
    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },
};
